import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { AuditRecord } from '@/api/types';
import { cn, formatDate } from '@/lib/utils';
import { parsePrompt } from './prompt';

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'pending';

interface Step {
  key: string;
  title: string;
  tone: Tone;
  at?: string | null;
  body?: React.ReactNode;
}

const TONE_DOT: Record<Tone, string> = {
  neutral: 'bg-primary',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-destructive',
  pending: 'border-2 border-muted-foreground/50 bg-background',
};

function Quote({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="max-h-48 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/30 px-3 py-2 text-sm text-foreground">
      {children}
    </div>
  );
}

function requestBody(entry: AuditRecord): React.ReactNode {
  const { text, context } = parsePrompt(entry.prompt);
  return (
    <div className="space-y-1.5">
      {text ? <Quote>{text}</Quote> : null}
      {context.length ? (
        <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-xs text-muted-foreground">
          {context.map(([k, v]) => (
            <span key={k}>
              {k}: <span className="text-foreground">{v}</span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function buildSteps(entry: AuditRecord, t: TFunction): Step[] {
  const { status } = entry;
  const request: Step = { key: 'request', title: t('audit.journey.request'), tone: 'neutral', at: entry.createdAt, body: requestBody(entry) };

  if (status === 'config_change') {
    return [{ ...request, title: t('audit.journey.configChange') }];
  }
  if (status === 'error') {
    return [request, { key: 'error', title: t('audit.journey.error'), tone: 'danger', body: entry.error }];
  }

  const steps: Step[] = [request];

  if (status === 'answer_given' || status === 'answer_satisfied' || status === 'answer_escalated') {
    steps.push({
      key: 'answer',
      title: t('audit.journey.answer'),
      tone: 'neutral',
      body: entry.response ? <Quote>{entry.response}</Quote> : null,
    });
    if (status === 'answer_given') {
      steps.push({ key: 'awaiting', title: t('audit.journey.awaitingFeedback'), tone: 'pending' });
    }
    if (status === 'answer_satisfied') {
      steps.push({ key: 'satisfied', title: t('audit.journey.satisfied'), tone: 'success', at: entry.decidedAt });
    }
    if (status === 'answer_escalated') {
      steps.push({ key: 'unsatisfied', title: t('audit.journey.unsatisfied'), tone: 'warning', at: entry.decidedAt, body: entry.comment });
    }
  } else {
    steps.push({
      key: 'proposed',
      title: t('audit.journey.actionProposed'),
      tone: 'neutral',
      body: (
        <div className="space-y-1.5">
          {entry.actionSummary ? <div className="text-sm font-medium text-foreground">{entry.actionSummary}</div> : null}
          {entry.response ? <Quote>{entry.response}</Quote> : null}
          {entry.actionName ? <div className="font-mono text-xs">{entry.actionName}</div> : null}
        </div>
      ),
    });
    if (status === 'action_proposed') {
      steps.push({ key: 'awaiting', title: t('audit.journey.awaitingDecision'), tone: 'pending' });
    }
    if (status === 'action_succeeded' || status === 'action_failed' || status === 'action_failed_escalated') {
      steps.push({ key: 'accepted', title: t('audit.journey.accepted'), tone: 'neutral', at: entry.decidedAt });
      steps.push(
        status === 'action_succeeded'
          ? { key: 'succeeded', title: t('audit.journey.succeeded'), tone: 'success', at: entry.executedAt, body: entry.actionResult }
          : { key: 'failed', title: t('audit.journey.failed'), tone: 'danger', at: entry.executedAt, body: entry.error },
      );
    }
    if (status === 'action_rejected' || status === 'action_rejected_escalated') {
      steps.push({
        key: 'rejected',
        title: t('audit.journey.rejected'),
        tone: status === 'action_rejected' ? 'neutral' : 'warning',
        at: entry.decidedAt,
        body: entry.comment,
      });
    }
  }

  if (status === 'answer_escalated' || status === 'action_failed_escalated' || status === 'action_rejected_escalated') {
    steps.push({
      key: 'escalated',
      title: t('audit.journey.escalated'),
      tone: 'warning',
      at: entry.escalatedAt,
      body: (
        <div className="space-y-0.5">
          <div>
            {entry.zohoTicketId
              ? t('audit.journey.zohoTicket', { id: entry.zohoTicketId })
              : t('audit.journey.escalatedOutsideZorro')}
          </div>
          {status === 'action_failed_escalated' && entry.comment ? <div>{entry.comment}</div> : null}
        </div>
      ),
    });
  }

  return steps;
}

export function AuditJourney({ entry }: { entry: AuditRecord }): JSX.Element {
  const { t } = useTranslation();
  const steps = buildSteps(entry, t);
  return (
    <ol className="relative ml-1.5 space-y-4 border-l border-border pl-5">
      {steps.map((step) => (
        <li key={step.key} className="relative">
          <span
            className={cn(
              'absolute -left-[27px] top-1 h-3 w-3 rounded-full ring-4 ring-background',
              TONE_DOT[step.tone],
            )}
          />
          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
            <span className={cn('text-sm font-medium', step.tone === 'pending' && 'italic text-muted-foreground')}>
              {step.title}
            </span>
            {step.at ? <span className="text-xs text-muted-foreground">{formatDate(step.at)}</span> : null}
          </div>
          {step.body ? <div className="mt-1.5 text-sm text-muted-foreground">{step.body}</div> : null}
        </li>
      ))}
    </ol>
  );
}
